import { GoogleMap } from "@/shared/components/common/GoogleMap";
import type { Step1Props } from "../../model/propertiesTypes";
import { useStep1ViewModel } from "../../viewmodel/useStep1ViewModel";
import { Modal } from "@/shared/modal";
import styles from "./Step1.module.css";
import andyHdLogo from "@/assets/andyhdlogo.png";

/**
 * Step 1: Property Details & Overview
 * Displays property information, valuation, and community safety data
 */
export const Step1 = ({ addressData, propertyDetails, onNext, onBack }: Step1Props) => {
  const {
    activeTab,
    setActiveTab,
    isMapModalOpen,
    setIsMapModalOpen,
    sexOffendersData,
    crimeIncidentsData,
    loading,
    error,
    mapMarkers,
    addressString,
    city,
    state,
    assessedValue,
    marketValue,
    calculatedPotentialSavings,
    handleBackClick,
    handleFileProtest,
  } = useStep1ViewModel({ addressData, propertyDetails, onNext, onBack });

  return (
    <div className={styles.propertiesPage}>
      {/* Header Section */}
      <header className={styles.header}>
        <button className={styles.backButton} onClick={handleBackClick} aria-label="Go back">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className={styles.logoContainer}>
          <div className={styles.logoCircle}>
            <img src={andyHdLogo} alt="Andy logo" className={styles.logoImage} />
          </div>
          <span className={styles.logoText}>ANDY</span>
        </div>

        <p className={styles.description}>
          It&apos;s about you and your property, having impact your final savings results such as
          exemptions, property condition, etc.
        </p>

        {/* Progress Bar */}
        <div className={styles.progressBar}>
          <div className={`${styles.progressStep} ${styles.progressStepCompleted}`}>
            <div className={styles.progressStepIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" fill="#10b981" stroke="#10b981" strokeWidth="2" />
                <path
                  d="M6 10l3 3 5-6"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className={styles.progressStepText}>Estimation Savings</span>
          </div>
          <div className={`${styles.progressStep} ${styles.progressStepCompleted}`}>
            <div className={styles.progressStepIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" fill="#3b82f6" stroke="#3b82f6" strokeWidth="2" />
                <path
                  d="M6 10l3 3 5-6"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className={styles.progressStepText}>Owner Details</span>
          </div>
          <div className={`${styles.progressStep} ${styles.progressStepCompleted}`}>
            <div className={styles.progressStepIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" fill="#3b82f6" stroke="#3b82f6" strokeWidth="2" />
                <path
                  d="M6 10l3 3 5-6"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className={styles.progressStepText}>User Type</span>
          </div>
        </div>

        {/* Step Number Indicator - Large "01" */}
        <div className={styles.stepNumber}>01</div>
      </header>

      {/* Main Content */}
      <div className={styles.contentWrapper}>
        <div className={styles.contentContainer}>
          {/* Main Property Card */}
          <div className={styles.mainCard}>
            {/* Address Section */}
            <div className={styles.addressSection}>
              <span className={styles.locationTag}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  style={{ marginRight: "6px" }}
                >
                  <path
                    d="M7 0C3.13 0 0 3.13 0 7c0 4.25 7 11 7 11s7-6.75 7-11c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                    fill="currentColor"
                  />
                </svg>
                {city}, {state}
              </span>
              <h1 className={styles.propertyAddress}>{addressString}</h1>
              <h2 className={styles.sectionTitle}>Property Details & Overview</h2>
            </div>

            {/* Valuation Summary */}
            <div className={styles.valuationBox}>
              <h3 className={styles.valuationTitle}>$ Valuation Summary</h3>
              <div className={styles.valuationValues}>
                <div className={styles.valuationItem}>
                  <span className={styles.valuationLabel}>Assessed Value</span>
                  <span className={styles.valuationAmount}>${assessedValue}</span>
                </div>
                <div className={styles.valuationDivider}></div>
                <div className={styles.valuationItem}>
                  <span className={styles.valuationLabel}>Market Value</span>
                  <span className={styles.valuationAmount}>${marketValue}</span>
                </div>
              </div>
            </div>

            {/* Protest Opportunity */}
            <div className={styles.protestBox}>
              <div className={styles.protestHeader}>
                <h3 className={styles.protestTitle}>Protest Opportunity</h3>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M7 14l5-5 5 5"
                    stroke="#065f46"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className={styles.savingsAmount}>${calculatedPotentialSavings}</div>
              <div className={styles.savingsLabel}>Potential savings</div>
              <p className={styles.protestDescription}>
                Based on comparable properties in your neighborhood, your property may be
                over-assessed.
              </p>
            </div>
          </div>

          {/* Community Safety Panel */}
          <div className={styles.safetyPanel}>
            <div className={styles.safetyHeader}>
              <h3 className={styles.safetyTitle}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  style={{ marginRight: "8px" }}
                >
                  <path
                    d="M10 1L2 5v6c0 5.55 3.84 10.74 8 12 4.16-1.26 8-6.45 8-12V5l-8-4z"
                    fill="currentColor"
                  />
                </svg>
                Community Safety
              </h3>
              <span className={styles.safeAreaTag}>Safe Area</span>
            </div>

            {/* Toggle Buttons */}
            <div className={styles.toggleButtons}>
              <button
                className={`${styles.toggleButton} ${activeTab === "crime" ? styles.toggleButtonActive : ""}`}
                onClick={() => setActiveTab("crime")}
              >
                Crime Report
              </button>
              <button
                className={`${styles.toggleButton} ${activeTab === "offenders" ? styles.toggleButtonActive : ""}`}
                onClick={() => setActiveTab("offenders")}
              >
                Sex Offenders
              </button>
            </div>

            {/* Recent Records */}
            <div className={styles.recordsSection}>
              {loading ? (
                <div className={styles.recordsList}>
                  <div className={styles.recordItem}>
                    <div className={styles.recordDetails}>
                      <div className={styles.recordName}>Loading...</div>
                    </div>
                  </div>
                </div>
              ) : error ? (
                <div className={styles.recordsList}>
                  <div className={styles.recordItem}>
                    <div className={styles.recordDetails}>
                      <div className={styles.recordName} style={{ color: "#ef4444" }}>
                        {error}
                      </div>
                    </div>
                  </div>
                </div>
              ) : activeTab === "offenders" ? (
                <div className={styles.recordsList}>
                  {sexOffendersData?.sex_offenders && sexOffendersData.sex_offenders.length > 0 ? (
                    sexOffendersData.sex_offenders.map((offender, index) => {
                      const fullName =
                        offender.sex_offender_name ||
                        `${offender.sex_offender_first_name || ""} ${offender.sex_offender_middle_name || ""} ${offender.sex_offender_last_name || ""}`.trim() ||
                        "Unknown";
                      const age = offender.sex_offender_age || "N/A";
                      const sex = offender.sex_offender_sex || "N/A";
                      const imageUrl = offender.sex_offender_image_url;

                      return (
                        <div key={offender.sex_offender_id || index} className={styles.recordItem}>
                          <div className={styles.recordIcon}>
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={fullName}
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                                onError={(e) => {
                                  // Fallback to default icon if image fails to load
                                  e.currentTarget.style.display = "none";
                                  e.currentTarget.nextElementSibling?.setAttribute(
                                    "style",
                                    "display: block"
                                  );
                                }}
                              />
                            ) : null}
                            <svg
                              width="40"
                              height="40"
                              viewBox="0 0 40 40"
                              fill="none"
                              style={{ display: imageUrl ? "none" : "block" }}
                            >
                              <circle cx="20" cy="20" r="20" fill="#e5e7eb" />
                              <path
                                d="M20 10c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 10c-3.31 0-6 2.69-6 6h12c0-3.31-2.69-6-6-6z"
                                fill="#9ca3af"
                              />
                            </svg>
                          </div>
                          <div className={styles.recordDetails}>
                            <div className={styles.recordName}>{fullName}</div>
                            <div className={styles.recordInfo}>
                              {sex} • Age: {age}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles.recordItem}>
                      <div className={styles.recordDetails}>
                        <div className={styles.recordName}>No sex offenders found in this area</div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.recordsList}>
                  {crimeIncidentsData?.incidents && crimeIncidentsData.incidents.length > 0 ? (
                    crimeIncidentsData.incidents.map((incident, index) => {
                      const offense =
                        incident.incident_offense_description ||
                        incident.incident_offense ||
                        "Unknown";
                      const date = incident.incident_date
                        ? new Date(incident.incident_date).toLocaleDateString()
                        : "Date unknown";
                      const address = incident.incident_address || "Address unknown";

                      return (
                        <div key={incident.incident_code || index} className={styles.recordItem}>
                          <div className={styles.recordIcon}>
                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                              <circle cx="20" cy="20" r="20" fill="#fee2e2" />
                              <path
                                d="M20 12v8m0 4h.01M20 10c-5.523 0-10 4.477-10 10s4.477 10 10 10 10-4.477 10-10-4.477-10-10-10z"
                                stroke="#dc2626"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                          </div>
                          <div className={styles.recordDetails}>
                            <div className={styles.recordName}>{offense}</div>
                            <div className={styles.recordInfo}>
                              {date} • {address}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles.recordItem}>
                      <div className={styles.recordDetails}>
                        <div className={styles.recordName}>
                          No crime incidents found in this area
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Map Section */}
            <div className={styles.mapSection}>
              <div className={styles.mapContainer}>
                <GoogleMap
                  address={addressString}
                  zoom={14}
                  center={
                    propertyDetails?.propertyInfo?.latitude &&
                    propertyDetails?.propertyInfo?.longitude
                      ? {
                          lat: Number(propertyDetails.propertyInfo.latitude),
                          lng: Number(propertyDetails.propertyInfo.longitude),
                        }
                      : undefined
                  }
                  markers={mapMarkers}
                />
                <button
                  type="button"
                  className={styles.mapExpandButton}
                  onClick={() => setIsMapModalOpen(true)}
                  aria-label="Open larger community map"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 14v5a1 1 0 001 1h5M19 10V5a1 1 0 00-1-1h-5M9 4H5m0 0v4M20 20v-4m0 0h-4"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
              <div className={styles.mapLabel}>
                Community Data
                <div className={styles.mapAddress}>
                  {activeTab === "crime"
                    ? `${crimeIncidentsData?.incidents_count || 0} Crime Incidents`
                    : `${sexOffendersData?.sex_offenders_count || 0} Sex Offenders`}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isMapModalOpen}
        onClose={() => setIsMapModalOpen(false)}
        showCloseButton={false}
        unconstrained
        className={styles.mapModal}
        bodyClassName={styles.mapModalBodyInner}
        ariaLabel="Community map"
      >
        <button
          type="button"
          className={styles.mapModalClose}
          onClick={() => setIsMapModalOpen(false)}
          aria-label="Close map"
        >
          ✕
        </button>
        <div className={styles.mapModalContent}>
          <GoogleMap
            address={addressString}
            zoom={14}
            center={
              propertyDetails?.propertyInfo?.latitude && propertyDetails?.propertyInfo?.longitude
                ? {
                    lat: Number(propertyDetails.propertyInfo.latitude),
                    lng: Number(propertyDetails.propertyInfo.longitude),
                  }
                : undefined
            }
            markers={mapMarkers}
            className={styles.fullscreenMap}
          />
        </div>
      </Modal>

      {/* Bottom CTA and Navigation */}
      <div className={styles.bottomSection}>
        <button className={styles.protestButton} onClick={handleFileProtest}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            style={{ marginRight: "8px" }}
          >
            <path
              d="M10 1L2 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
            />
            <path d="M7 10l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          FILE PROTEST NOW!
        </button>
      </div>
    </div>
  );
};
